<template>
  <div class="page-container">
    <el-card shadow="never">
      <div class="toolbar">
        <span class="section-title">店铺管理员管理</span>
        <div style="display: flex; gap: 8px;" v-if="selectedShopId">
          <el-button type="primary" size="small" @click="handleGenerateInvite" :loading="inviteLoading">
            生成邀请码
          </el-button>
          <el-button size="small" @click="handleRefresh">
            <i class="el-icon-refresh" />
            刷新
          </el-button>
        </div>
      </div>

      <div v-if="selectedShop" class="shop-info">
        <div class="shop-info-item">
          <i class="el-icon-s-shop"></i>
          <span class="shop-info-name">{{ selectedShop.name }}</span>
        </div>
        <div class="shop-info-item">
          <i class="el-icon-location-outline"></i>
          <span>{{ selectedShop.address }}{{ selectedShop.city ? '，' + selectedShop.city : '' }}</span>
        </div>
      </div>

      <div style="margin-top: 16px;">
        <el-select
          v-model="selectedShopId"
          placeholder="请选择店铺"
          filterable
          size="small"
          style="width: 280px;"
          @change="handleShopChange"
        >
          <el-option
            v-for="shop in shopList"
            :key="shop.id"
            :label="shop.name"
            :value="shop.id"
          />
        </el-select>

        <el-select
          v-if="selectedShopId"
          v-model="statusFilter"
          placeholder="筛选状态"
          size="small"
          style="width: 140px; margin-left: 12px;"
          @change="handleFilterChange"
          clearable
        >
          <el-option label="待审核" :value="0" />
          <el-option label="生效中" :value="1" />
          <el-option label="已拒绝" :value="2" />
          <el-option label="已失效" :value="3" />
        </el-select>

        <el-input
          v-if="selectedShopId"
          v-model="searchKeyword"
          placeholder="搜索姓名/昵称/手机号"
          size="small"
          style="width: 240px; margin-left: 12px;"
          clearable
        />
      </div>

      <el-table
        v-if="selectedShopId"
        :data="filteredAdmins"
        v-loading="loading"
        border
        stripe
        style="width: 100%; margin-top: 16px;"
        empty-text="暂无管理员记录"
      >
        <el-table-column prop="applicantName" label="申请人姓名" width="120" />
        <el-table-column prop="nickname" label="用户昵称" min-width="120" />
        <el-table-column prop="phone" label="手机号" width="130" />
        <el-table-column label="状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag size="small" :type="statusType(scope.row.status)">
              {{ statusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="申请/绑定时间" width="170" align="center">
          <template slot-scope="scope">
            {{ scope.row.createdAt ? scope.row.createdAt.substring(0, 16).replace('T', ' ') : '-' }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="220" align="center" fixed="right">
          <template slot-scope="scope">
            <!-- 待审核 -->
            <template v-if="scope.row.status === 0">
              <el-button type="text" size="mini" @click="handleRemove(scope.row)" style="color: #999;">移除</el-button>
              <el-button type="text" size="mini" @click="handleReview(scope.row, false)">拒绝</el-button>
              <el-button type="text" size="mini" @click="handleReview(scope.row, true)" style="color: #67C23A;">通过</el-button>
            </template>
            <!-- 生效中 -->
            <template v-else-if="scope.row.status === 1">
              <el-button type="text" size="mini" @click="handleToggle(scope.row)" style="color: #E6A23C;">设为失效</el-button>
            </template>
            <!-- 已失效 -->
            <template v-else-if="scope.row.status === 3">
              <el-button type="text" size="mini" @click="handleToggle(scope.row)">重新生效</el-button>
            </template>
            <!-- 已拒绝 -->
            <template v-else-if="scope.row.status === 2">
              <el-button type="text" size="mini" @click="handleRemove(scope.row)" style="color: #999;">移除</el-button>
            </template>
          </template>
        </el-table-column>
      </el-table>

      <div v-if="!selectedShopId" class="empty-hint">
        <i class="el-icon-info" />
        <span>请先选择一个店铺</span>
      </div>
    </el-card>

    <!-- 邀请码弹窗 -->
    <el-dialog title="邀请二维码" :visible.sync="inviteVisible" width="420px" center>
      <div v-if="inviteInfo" class="invite-content">
        <div class="qr-wrapper">
          <img v-if="inviteInfo.qrCodeUrl" :src="inviteInfo.qrCodeUrl" alt="邀请二维码" class="qr-image" />
          <div v-else class="qr-placeholder">
            <i class="el-icon-picture-outline" />
            <span>暂无二维码</span>
          </div>
        </div>
        <div class="invite-meta">
          <p><strong>邀请码：</strong><code>{{ inviteInfo.inviteCode }}</code></p>
          <p><strong>有效期至：</strong>{{ inviteInfo.expireAt || '永久有效' }}</p>
          <p><strong>状态：</strong>
            <el-tag :type="inviteInfo.status === 1 ? 'info' : 'success'" size="small">
              {{ inviteInfo.status === 1 ? '已使用' : '有效' }}
            </el-tag>
          </p>
        </div>
      </div>
      <div slot="footer">
        <el-button @click="handleInviteClose">关 闭</el-button>
        <el-button type="primary" @click="copyInviteCode">复制邀请码</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getShopList, getShopAdmins, removeShopAdmin, generateInvite, reviewAdmin, toggleAdminStatus } from '@/api/shop'

const STATUS_MAP = {
  0: { text: '待审核', type: 'warning' },
  1: { text: '生效中', type: 'success' },
  2: { text: '已拒绝', type: 'danger' },
  3: { text: '已失效', type: 'info' }
}

export default {
  name: 'AdminList',
  data() {
    return {
      shopList: [],
      selectedShopId: null,
      admins: [],
      statusFilter: null,
      searchKeyword: '',
      loading: false,
      inviteVisible: false,
      inviteInfo: null,
      inviteLoading: false
    }
  },
  computed: {
    selectedShop() {
      if (!this.selectedShopId) return null
      return this.shopList.find(shop => shop.id === this.selectedShopId) || null
    },
    filteredAdmins() {
      let result = this.admins
      
      // 状态筛选
      if (this.statusFilter !== null && this.statusFilter !== '') {
        result = result.filter(a => a.status === this.statusFilter)
      }
      
      // 关键词搜索
      if (this.searchKeyword) {
        const keyword = this.searchKeyword.toLowerCase()
        result = result.filter(a => {
          return (a.applicantName && a.applicantName.toLowerCase().includes(keyword)) ||
                 (a.nickname && a.nickname.toLowerCase().includes(keyword)) ||
                 (a.phone && a.phone.includes(keyword))
        })
      }
      
      return result
    }
  },
  created() {
    this.loadShops()
  },
  methods: {
    statusText(status) {
      const item = STATUS_MAP[status]
      return item ? item.text : '未知'
    },
    statusType(status) {
      const item = STATUS_MAP[status]
      return item ? item.type : ''
    },
    async loadShops() {
      try {
        const res = await getShopList({ page: 1, size: 999 })
        this.shopList = (res.data && res.data.records) || []
        if (this.shopList.length > 0) {
          this.selectedShopId = this.shopList[0].id
          this.handleShopChange(this.selectedShopId)
        }
      } catch (e) {
        console.error(e)
      }
    },
    async handleShopChange(shopId) {
      if (!shopId) return
      this.loading = true
      try {
        const res = await getShopAdmins(shopId)
        const list = res.data || []
        // 排序：待审核 → 生效 → 已失效 → 已拒绝
        const order = { 0: 0, 1: 1, 3: 2, 2: 3 }
        list.sort((a, b) => (order[a.status] != null ? order[a.status] : 9) - (order[b.status] != null ? order[b.status] : 9))
        this.admins = list
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    handleRefresh() {
      if (this.selectedShopId) {
        this.handleShopChange(this.selectedShopId)
      }
    },
    handleFilterChange() {
      // 仅触发计算属性重新筛选
    },
    async handleReview(row, approved) {
      try {
        await this.$confirm(
          approved
            ? '确定要通过「' + (row.applicantName || row.nickname || '该用户') + '」的管理员申请吗？'
            : '确定要拒绝「' + (row.applicantName || row.nickname || '该用户') + '」的管理员申请吗？',
          '提示',
          { type: 'warning' }
        )
        await reviewAdmin(row.id, approved)
        this.$message.success(approved ? '审核通过' : '已拒绝')
        this.handleShopChange(this.selectedShopId)
      } catch (e) {
        if (e !== 'cancel' && e !== 'close') {
          console.error(e)
        }
      }
    },
    async handleToggle(row) {
      const isActive = row.status === 1
      try {
        await this.$confirm(
          isActive ? '确定要停用该管理员吗？' : '确定要重新启用该管理员吗？',
          '提示',
          { type: 'warning' }
        )
        await toggleAdminStatus(row.id)
        this.$message.success(isActive ? '已设为失效' : '已重新生效')
        this.handleShopChange(this.selectedShopId)
      } catch (e) {
        if (e !== 'cancel' && e !== 'close') {
          console.error(e)
        }
      }
    },
    async handleRemove(row) {
      try {
        await this.$confirm('确定要移除该管理员记录吗？此操作不可撤销。', '提示', {
          type: 'warning'
        })
        await removeShopAdmin(this.selectedShopId, row.userId)
        this.$message.success('移除成功')
        this.handleShopChange(this.selectedShopId)
      } catch (e) {
        if (e !== 'cancel' && e !== 'close') {
          console.error(e)
        }
      }
    },
    async handleGenerateInvite() {
      this.inviteLoading = true
      try {
        const res = await generateInvite(this.selectedShopId)
        this.inviteInfo = res.data
        this.inviteVisible = true
      } catch (e) {
        console.error(e)
        this.$message.error('生成邀请码失败')
      } finally {
        this.inviteLoading = false
      }
    },
    copyInviteCode() {
      if (this.inviteInfo && this.inviteInfo.inviteCode) {
        const el = document.createElement('textarea')
        el.value = this.inviteInfo.inviteCode
        document.body.appendChild(el)
        el.select()
        document.execCommand('copy')
        document.body.removeChild(el)
        this.$message.success('邀请码已复制到剪贴板')
      }
    },
    handleInviteClose() {
      this.inviteVisible = false
      this.inviteInfo = null
      if (this.selectedShopId) {
        this.handleShopChange(this.selectedShopId)
      }
    }
  }
}
</script>

<style scoped>
.page-container {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f1f1f;
}

.shop-info {
  display: flex;
  align-items: center;
  gap: 24px;
  margin-top: 16px;
  padding: 12px 16px;
  background: #f0f9ff;
  border-radius: 6px;
  border: 1px solid #bae0ff;
  color: #1f1f1f;
  font-size: 14px;
}

.shop-info-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.shop-info-name {
  font-weight: 600;
  font-size: 15px;
}

.shop-info-item i {
  font-size: 16px;
  color: #409eff;
}

.empty-hint {
  margin-top: 40px;
  text-align: center;
  color: #bfbfbf;
  font-size: 14px;
}

.empty-hint i {
  margin-right: 8px;
}

.invite-content {
  text-align: center;
}

.qr-wrapper {
  width: 200px;
  height: 200px;
  margin: 0 auto 20px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.qr-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.qr-placeholder {
  color: #bfbfbf;
  font-size: 14px;
  text-align: center;
}

.qr-placeholder i {
  font-size: 48px;
  display: block;
  margin-bottom: 8px;
}

.invite-meta {
  text-align: left;
  background: #fafafa;
  padding: 16px;
  border-radius: 6px;
}

.invite-meta p {
  margin-bottom: 8px;
  font-size: 14px;
  color: #595959;
}

.invite-meta p:last-child {
  margin-bottom: 0;
}
</style>
